import { useEffect, useRef, type RefObject } from 'react';

/**
 * True only once this component instance has genuinely unmounted — never on an effect re-run
 * for an unrelated reason (a dependency identity churning, StrictMode's dev-only
 * mount→cleanup→remount). Callers that gate a promise `.then`/`.catch` on this (rather than a
 * per-effect-run `ignore` flag set in that same effect's own cleanup) avoid silencing a callback
 * whose triggering effect happened to re-run for a reason that has nothing to do with whether
 * the callback is still relevant.
 *
 * Resetting `false` on setup (not just the `useRef` initializer) matters under StrictMode's
 * dev-only double-invoke: without it, the synthetic cleanup would flip this `true` and nothing
 * would ever flip it back for the component's actual, still-mounted lifetime.
 */
export default function useIsUnmountedRef(): RefObject<boolean> {
	const isUnmountedRef = useRef(false);
	useEffect(() => {
		isUnmountedRef.current = false;
		return () => {
			isUnmountedRef.current = true;
		};
	}, []);
	return isUnmountedRef;
}
